import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Star, ExternalLink, MapPin } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Featured Listings grid — admin-featured businesses sorted by priority (desc)
 * and shown BEFORE any normal listings on Discover.
 */
export default function FeaturedListings({ category, city, limit = 4, className = "" }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const params = {};
    if (category) params.category = category;
    if (city) params.city = city;
    if (limit) params.limit = limit;
    api.get("/featured-listings", { params })
      .then((r) => setRows(r.data.listings || []))
      .catch(() => setRows([]));
  }, [category, city, limit]);

  if (rows.length === 0) return null;

  return (
    <section data-testid="featured-listings" className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2 px-1">
        <Star size={14} className="text-[#F5C462] fill-[#F5C462]"/>
        <p className="text-[11px] uppercase tracking-[0.15em] text-[#F5C462] font-semibold">Featured</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {rows.map((f) => (
          <FeaturedCard key={f.id} listing={f}/>
        ))}
      </div>
    </section>
  );
}

function FeaturedCard({ listing }) {
  const coverUrl = listing.cover_path ? `${BACKEND}/api/featured-listings/media/${listing.cover_path}` : "";
  const logoUrl = listing.logo_path ? `${BACKEND}/api/featured-listings/media/${listing.logo_path}` : "";
  const open = () => window.open(listing.website_url, "_blank", "noopener,noreferrer");
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
      data-testid={`featured-listing-${listing.id}`}
      className="rounded-2xl overflow-hidden border border-[#F5C462]/30 bg-gradient-to-b from-[#180D0D] to-[#0A0605] hover:border-[#F5C462]/60 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#F5C462]/50"
    >
      {coverUrl && (
        <div className="h-24 w-full bg-black overflow-hidden">
          <img src={coverUrl} alt={listing.business_name} loading="lazy" className="w-full h-full object-cover"/>
        </div>
      )}
      <div className="p-3">
        <div className="flex items-center gap-2">
          {logoUrl && <img src={logoUrl} alt="" loading="lazy" className="w-8 h-8 rounded-lg object-cover border border-zinc-800 shrink-0"/>}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate flex items-center gap-1">
              {listing.business_name}
              <ExternalLink size={10} className="text-zinc-400"/>
            </p>
            <p className="text-[10px] text-zinc-400 truncate flex items-center gap-1">
              <MapPin size={9}/>{listing.city}{listing.state ? `, ${listing.state}` : ""}
            </p>
          </div>
        </div>
        <p className="text-[11px] text-zinc-300 mt-1.5 line-clamp-2 leading-snug">{listing.description}</p>
      </div>
    </div>
  );
}
